import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Users, FileText, Edit, ChevronDown, ChevronRight, TrendingUp, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const Commercials = () => {
  const { t } = useLanguage();
  const [commercials, setCommercials] = useState([]);
  const [recap, setRecap] = useState([]);
  const [commissionsHistory, setCommissionsHistory] = useState([]);
  const [tokensRecap, setTokensRecap] = useState([]);
  const [tokenDialog, setTokenDialog] = useState(null);
  const [tokenAmount, setTokenAmount] = useState('');
  const [top5, setTop5] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCommercial, setEditingCommercial] = useState(null);
  const [expandedCommercials, setExpandedCommercials] = useState({});
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', zone: '', commission_rate: '10'
  });
  const [editFormData, setEditFormData] = useState({
    email: '', phone: '', city: '', zone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [commercialsRes, recapRes, historyRes, top5Res, tokensRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commercials`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/commercial-recap`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commercial-commissions-history`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commercial-top5`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/design/admin/tokens-recap`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCommercials(commercialsRes.data);
      setRecap(recapRes.data);
      setCommissionsHistory(historyRes.data);
      setTop5(top5Res.data);
      setTokensRecap(tokensRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTokens = async () => {
    if (!tokenDialog || !tokenAmount || parseInt(tokenAmount) <= 0) return;
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/design/admin/tokens/${tokenDialog.id}`,
        { tokens: parseInt(tokenAmount) }, { withCredentials: true });
      toast.success(res.data.message);
      setTokenDialog(null);
      setTokenAmount('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/commercials`,
        { ...formData, commission_rate: parseFloat(formData.commission_rate) },
        { withCredentials: true }
      );
      toast.success('Commercial ajouté avec succès');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingCommercial) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/commercials/${editingCommercial.id}`,
        editFormData,
        { withCredentials: true }
      );
      toast.success('Commercial modifié avec succès');
      setEditDialogOpen(false);
      setEditingCommercial(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleEdit = (c) => {
    setEditingCommercial(c);
    setEditFormData({
      email: c.email || '',
      phone: c.phone || '',
      city: c.city || '',
      zone: c.zone || ''
    });
    setEditDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commercial ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/commercials/${id}`, { withCredentials: true });
      toast.success('Commercial supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', phone: '', zone: '', commission_rate: '10' });
  };

  const toggleCommercial = (id) => {
    setExpandedCommercials(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Commerciaux</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion des comptes commerciaux et commissions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-commercial-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Nouveau commercial
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau commercial</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom*</Label>
                  <Input data-testid="commercial-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Jean Dupont" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email*</Label>
                    <Input data-testid="commercial-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe*</Label>
                    <Input data-testid="commercial-password-input" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input data-testid="commercial-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Zone couverte</Label>
                    <Input data-testid="commercial-zone-input" value={formData.zone} onChange={(e) => setFormData({ ...formData, zone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Commission appliquée*</Label>
                  <Select value={formData.commission_rate} onValueChange={(value) => setFormData({ ...formData, commission_rate: value })}>
                    <SelectTrigger data-testid="commercial-commission-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                      <SelectItem value="40">40%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-commercial-button" type="submit" className="bg-slate-900 hover:bg-slate-800">Créer le compte</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Top 5 Classement */}
        <Card data-testid="top5-commercials-card" className="border-slate-200 shadow-sm mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp size={20} className="text-amber-600" />
              <CardTitle className="text-lg font-medium">Classement Top 5 — Chiffre d'Affaires</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {top5.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Aucune donnée</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Nom commercial</TableHead>
                    <TableHead>Zone</TableHead>
                    <TableHead className="text-right">CA mois en cours</TableHead>
                    <TableHead className="text-right">CA année</TableHead>
                    <TableHead className="text-right">Commission mois</TableHead>
                    <TableHead className="text-right">Commission année</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top5.map((c, i) => (
                    <TableRow key={c.id || `top5-${i}`} data-testid={`top5-row-${i}`} className={i === 0 ? 'bg-amber-50' : ''}>
                      <TableCell className="font-bold text-lg">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-slate-600">{c.zone || '—'}</TableCell>
                      <TableCell className="text-right font-semibold text-blue-700">{(c.month_ca || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell className="text-right font-semibold text-blue-900">{(c.year_ca || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell className="text-right font-semibold">{(c.month_commission || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{(c.year_commission || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Liste des commerciaux</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : commercials.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun commercial</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Nom</TableHead>
                    <TableHead className="font-medium">Email</TableHead>
                    <TableHead className="font-medium">Téléphone</TableHead>
                    <TableHead className="font-medium">Ville</TableHead>
                    <TableHead className="font-medium">Zone</TableHead>
                    <TableHead className="font-medium text-right">Commission</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commercials.map((c) => (
                    <TableRow key={c.id} data-testid="commercial-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.phone || '-'}</TableCell>
                      <TableCell>{c.city || '-'}</TableCell>
                      <TableCell>{c.zone || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-slate-100 text-slate-800 border border-slate-200">{c.commission_rate}%</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button data-testid="edit-commercial-button" size="sm" variant="ghost" onClick={() => handleEdit(c)} className="h-8 w-8 p-0">
                            <Edit size={16} />
                          </Button>
                          <Button data-testid="delete-commercial-button" size="sm" variant="ghost" onClick={() => handleDelete(c.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Commercial Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier {editingCommercial?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="edit-commercial-email" type="email" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="edit-commercial-phone" value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input data-testid="edit-commercial-city" value={editFormData.city} onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Zone</Label>
                <Input data-testid="edit-commercial-zone" value={editFormData.zone} onChange={(e) => setEditFormData({ ...editFormData, zone: e.target.value })} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
                <Button data-testid="submit-edit-commercial" type="submit" className="bg-slate-900 hover:bg-slate-800">Enregistrer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Card data-testid="commercial-recap-card" className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Récapitulatif hebdomadaire</CardTitle>
            </div>
            <p className="text-xs text-slate-500 mt-1">Aperçu des performances : CA et commissions par commercial</p>
          </CardHeader>
          <CardContent>
            {recap.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun commercial avec des commandes</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Commercial</TableHead>
                    <TableHead className="font-medium">Zone</TableHead>
                    <TableHead className="font-medium text-right">Cmd semaine</TableHead>
                    <TableHead className="font-medium text-right">CA semaine</TableHead>
                    <TableHead className="font-medium text-right">Com. semaine</TableHead>
                    <TableHead className="font-medium text-right">Cmd mois</TableHead>
                    <TableHead className="font-medium text-right">CA mois</TableHead>
                    <TableHead className="font-medium text-right">Com. mois</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recap.map((r) => (
                    <TableRow key={r.commercial_id} data-testid="recap-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        {r.name}<br /><span className="text-xs text-slate-500">{r.email}</span>
                      </TableCell>
                      <TableCell className="text-sm">{r.zone || '-'}</TableCell>
                      <TableCell className="text-right">{r.week_orders}</TableCell>
                      <TableCell className="text-right font-medium">{r.week_ca.toLocaleString('fr-FR')} &euro;</TableCell>
                      <TableCell className="text-right font-medium text-emerald-700">{r.week_commission.toLocaleString('fr-FR')} &euro;</TableCell>
                      <TableCell className="text-right">{r.month_orders}</TableCell>
                      <TableCell className="text-right font-medium">{r.month_ca.toLocaleString('fr-FR')} &euro;</TableCell>
                      <TableCell className="text-right font-medium text-emerald-700">{r.month_commission.toLocaleString('fr-FR')} &euro;</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* COMMISSIONS PAR MOIS / ANNÉE */}
        <Card data-testid="commissions-history-card" className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Commissions par mois / année</CardTitle>
            </div>
            <p className="text-xs text-slate-500 mt-1">Détail des commissions calculées automatiquement pour chaque commercial</p>
          </CardHeader>
          <CardContent>
            {commissionsHistory.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucune donnée disponible</p>
            ) : (
              <div className="space-y-3">
                {commissionsHistory.map((c) => (
                  <div key={c.commercial_id} data-testid="commission-commercial-block" className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      data-testid="toggle-commercial-history"
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      onClick={() => toggleCommercial(c.commercial_id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedCommercials[c.commercial_id] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                        <span className="font-semibold text-slate-900">{c.name}</span>
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">{c.commission_rate}%</Badge>
                        <span className="text-xs text-slate-500">{c.email}</span>
                      </div>
                      <span className="text-sm text-slate-500">{c.months.length} mois</span>
                    </button>
                    {expandedCommercials[c.commercial_id] && (
                      <div className="p-0">
                        {c.months.length === 0 ? (
                          <p className="text-center py-4 text-sm text-slate-500">Aucune commande</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead className="font-medium">Mois</TableHead>
                                <TableHead className="font-medium text-right">Commandes</TableHead>
                                <TableHead className="font-medium text-right">CA</TableHead>
                                <TableHead className="font-medium text-right">Commission</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {c.months.map((m) => (
                                <TableRow key={m.month_label} data-testid="commission-month-row" className="hover:bg-slate-50">
                                  <TableCell className="font-medium">{m.month_label}</TableCell>
                                  <TableCell className="text-right">{m.orders_count}</TableCell>
                                  <TableCell className="text-right font-medium">{m.ca.toLocaleString('fr-FR')} &euro;</TableCell>
                                  <TableCell className="text-right font-bold text-emerald-700">{m.commission.toLocaleString('fr-FR')} &euro;</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Tokens IA Recap */}
        <Card className="border-amber-200" data-testid="tokens-recap-section">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Coins size={20} className="text-amber-500" />
              <CardTitle className="text-base">Tokens Génération IA</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {tokensRecap.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-3">Aucun commercial</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50/50">
                    <TableHead className="font-medium">Commercial</TableHead>
                    <TableHead className="font-medium">Email</TableHead>
                    <TableHead className="font-medium text-center">Tokens</TableHead>
                    <TableHead className="font-medium text-center">Générations</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokensRecap.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium text-sm">{u.name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{u.email}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`border-0 text-xs ${u.design_tokens > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                          {u.design_tokens}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{u.design_generations}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                          onClick={() => { setTokenDialog(u); setTokenAmount('25'); }}>
                          <Coins size={12} /> Ajouter tokens
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Token Assignment Dialog */}
        <Dialog open={!!tokenDialog} onOpenChange={() => setTokenDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Coins size={18} className="text-amber-500" /> Ajouter des tokens</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Commercial : <strong>{tokenDialog?.name}</strong></p>
              <p className="text-xs text-slate-500">Solde actuel : {tokenDialog?.design_tokens || 0} tokens</p>
              <div>
                <Label className="text-sm font-medium">Nombre de tokens à ajouter</Label>
                <Input type="number" min="1" value={tokenAmount} onChange={e => setTokenAmount(e.target.value)} className="mt-1" />
              </div>
              <div className="flex gap-2">
                {[10, 25, 50, 100].map(n => (
                  <Button key={n} size="sm" variant="outline" className="text-xs" onClick={() => setTokenAmount(String(n))}>{n}</Button>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTokenDialog(null)}>Annuler</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleAssignTokens} disabled={!tokenAmount || parseInt(tokenAmount) <= 0}>Ajouter</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
